import { useState, useEffect } from "react";
import { toast } from "sonner";
import { LogOut } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TimeSelect } from "@/components/TimeSelect";
import { SingleDatePicker } from "@/components/SingleDatePicker";
import { api } from "@/lib/api";

// Type <-> kind mapping.
const TYPES = [
  { label: "Work Shift", kind: "shift" },
  { label: "Standby", kind: "standby" },
  { label: "Tour", kind: "tour" },
];
const kindToType = (s) => {
  if (s?.kind === "tour") return "Tour";
  if (s?.kind === "standby") return "Standby";
  if (s?.kind === "shift") return "Work Shift";
  // Only infer from the label when the shift has no explicit kind.
  if (/STANDBY/i.test(s?.label || "")) return "Standby";
  return "Work Shift";
};

// MM/DD/YYYY <-> yyyy-MM-dd (avoid new Date() timezone drift).
const toInputDate = (mdy) => {
  const m = String(mdy || "").match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  if (!m) return "";
  const y = m[3].length === 2 ? "20" + m[3] : m[3];
  return `${y}-${String(m[1]).padStart(2, "0")}-${String(m[2]).padStart(2, "0")}`;
};
const toStorageDate = (iso) => {
  const m = String(iso || "").match(/(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : "";
};

// shift: existing shift to edit, or null to create. defaultDate: yyyy-MM-dd string for new shifts.
export const ShiftEditor = ({ open, onOpenChange, shift, defaultDate, onSaved }) => {
  const isEdit = !!shift;
  const [dateStr, setDateStr] = useState("");
  const [name, setName] = useState("");
  const [type, setType] = useState("Work Shift");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [saving, setSaving] = useState(false);
  // Early Release: leaving a non-tour shift early on approved paid time (Vacation/Sick).
  const [showEarly, setShowEarly] = useState(false);
  const [erPayType, setErPayType] = useState("Vacation");
  const [erHours, setErHours] = useState("");
  const [erEnd, setErEnd] = useState("");
  const [erBusy, setErBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (shift) {
      setDateStr(toInputDate(shift.date));
      setName(shift.label || "");
      setType(kindToType(shift));
      setStart(shift.start_time || "");
      setEnd(shift.end_time || "");
    } else {
      setDateStr(defaultDate || "");
      setName("");
      setType("Work Shift");
      setStart("");
      setEnd("");
    }
    setShowEarly(false);
    setErPayType("Vacation");
    setErHours("");
    setErEnd("");
  }, [open, shift, defaultDate]);

  const save = async () => {
    if (!dateStr) { toast.error("Pick a date for the shift"); return; }
    const kind = (TYPES.find((t) => t.label === type) || TYPES[0]).kind;
    const label = name.trim() || type;
    const body = { date: toStorageDate(dateStr), label, kind, start_time: start || null, end_time: end || null };
    setSaving(true);
    try {
      if (isEdit) await api.updateShift(shift.id, body);
      else await api.createShifts([{ ...body, source: "manual" }]);
      toast.success(isEdit ? "Shift updated" : "Shift added");
      onSaved?.();
      onOpenChange(false);
    } catch {
      toast.error(isEdit ? "Couldn't update shift" : "Couldn't add shift");
    } finally {
      setSaving(false);
    }
  };

  const logEarlyRelease = async () => {
    const hrs = Number(erHours);
    if (!hrs || hrs <= 0) { toast.error("Enter how many paid hours you used"); return; }
    setErBusy(true);
    try {
      await api.createCallout({
        type: "Early Release",
        points: 0,
        date: toStorageDate(dateStr),
        pay_type: erPayType,
        shift_hours: hrs,
        hours_paid: hrs,
        source_kind: "shift",
        source_id: shift.id,
        source_label: name.trim() || type,
      });
      // Optionally shorten the shift's end time on the calendar.
      if (erEnd && erEnd !== end) {
        await api.updateShift(shift.id, { end_time: erEnd });
        setEnd(erEnd);
      }
      toast.success(`Logged ${hrs}h ${erPayType} early release`);
      setShowEarly(false); setErHours(""); setErEnd("");
      onSaved?.();
      onOpenChange(false);
    } catch {
      toast.error("Couldn't log early release");
    } finally {
      setErBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-zinc-800 bg-zinc-950 sm:max-w-md" data-testid="shift-editor-dialog">
        <DialogHeader>
          <DialogTitle className="text-zinc-50">{isEdit ? "Edit shift" : "Add a shift"}</DialogTitle>
          <DialogDescription className="text-zinc-400">Manually add or adjust a shift on your calendar.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3.5 py-1">
          <div>
            <Label className="text-xs text-zinc-400">Date</Label>
            <SingleDatePicker
              value={dateStr}
              onChange={setDateStr}
              testid="shift-editor-date"
              placeholder="Pick a date"
              className="mt-1 w-full"
            />
          </div>

          <div>
            <Label className="text-xs text-zinc-400">Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={type === "Standby" ? "Standby" : type === "Tour" ? "e.g. VIP Tour" : "e.g. Morning shift"}
              className="mt-1 h-10 border-zinc-800 bg-zinc-900 text-zinc-100"
              data-testid="shift-editor-name"
            />
          </div>

          <div>
            <Label className="text-xs text-zinc-400">Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="mt-1 h-10 border-zinc-800 bg-zinc-900 text-zinc-100" data-testid="shift-editor-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-zinc-800 bg-zinc-950">
                {TYPES.map((t) => (
                  <SelectItem key={t.kind} value={t.label} data-testid={`shift-type-${t.kind}`}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-zinc-400">Start time</Label>
              <TimeSelect value={start} onChange={setStart} placeholder="Start" testid="shift-editor-start" className="mt-1 h-10 border-zinc-800 bg-zinc-900 text-zinc-100" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">End time</Label>
              <TimeSelect value={end} onChange={setEnd} placeholder="End" testid="shift-editor-end" className="mt-1 h-10 border-zinc-800 bg-zinc-900 text-zinc-100" />
            </div>
          </div>

          {isEdit && type !== "Tour" && (
            <div className="rounded-xl border border-[#9B2226]/30 bg-[#9B2226]/10" data-testid="shift-early-section">
              {!showEarly ? (
                <button
                  type="button"
                  onClick={() => setShowEarly(true)}
                  className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm font-600 text-[#E0575B] transition-colors hover:bg-[#9B2226]/15"
                  data-testid="shift-early-toggle"
                >
                  <LogOut className="h-4 w-4" /> Leave early — apply paid time
                </button>
              ) : (
                <div className="space-y-3 p-3">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-600 uppercase tracking-wide text-[#E0575B]">Early release</p>
                    <button type="button" onClick={() => setShowEarly(false)} className="text-xs text-zinc-400 hover:text-zinc-200" data-testid="shift-early-cancel">Cancel</button>
                  </div>
                  <div>
                    <Label className="mb-1.5 block text-xs uppercase tracking-wide text-zinc-400">Paid with</Label>
                    <div className="flex gap-1.5">
                      {["Vacation", "Sick"].map((p) => (
                        <button
                          key={p}
                          type="button"
                          onClick={() => setErPayType(p)}
                          data-testid={`shift-early-paytype-${p.toLowerCase()}`}
                          className={`flex-1 rounded-lg border py-2 text-xs font-500 transition-colors ${erPayType === p ? "border-emerald-500 bg-emerald-500/15 text-emerald-300" : "border-zinc-800 bg-zinc-900 text-zinc-300 hover:bg-zinc-800"}`}
                        >{p}</button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-zinc-400">Paid hours</Label>
                      <Input type="number" step="0.25" min="0" inputMode="decimal" value={erHours} onChange={(e) => setErHours(e.target.value)} placeholder="e.g. 2" className="mt-1 h-10 border-zinc-800 bg-zinc-900 text-zinc-100" data-testid="shift-early-hours" />
                    </div>
                    <div>
                      <Label className="text-xs text-zinc-400">New end time (optional)</Label>
                      <TimeSelect value={erEnd} onChange={setErEnd} placeholder="Left at…" testid="shift-early-end" className="mt-1 h-10 border-zinc-800 bg-zinc-900 text-zinc-100" />
                    </div>
                  </div>
                  <Button onClick={logEarlyRelease} disabled={erBusy} className="w-full bg-[#9B2226] text-white hover:bg-[#7A1A1E]" data-testid="shift-early-save">
                    {erBusy ? "Logging…" : "Log early release"}
                  </Button>
                  <p className="text-[11px] text-zinc-500">Adds an ER entry to Time &amp; Attendance (no attendance point).</p>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-800 hover:text-zinc-100" data-testid="shift-editor-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#9B2226] text-white hover:bg-[#7A1A1E]" data-testid="shift-editor-save">
            {saving ? "Saving…" : isEdit ? "Save changes" : "Add shift"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
